import { Injectable } from '@angular/core';

import { Feature, Module, SubscriberService } from '@services/subscriber/subscriber.service';
// CVX-6069: import directly, not via the @services barrel — the barrel re-exports
// this service, so a barrel import here is undefined at module-eval (the static
// `allowedAccounts` initializer below reads AccountTypes) and crashes the test bundle.
import { AccountTypes } from '@services/accounts/accounts.service';
import { UserdataService } from '@services/userdata/userdata.service';
import { get, includes, isEmpty, some, has, each } from 'lodash';
import { Permission, PermissionsService } from '@services/permissions/permissions.service';

export interface IModuleAccess {
  module: Module;
  feature?: Feature;
  checkRelatedFeatures?: boolean;
  subscriberAccess?: IModuleSubscriberAccess;
}

export interface IModuleSubscriberAccess {
  user?: string;
  account?: string;
  permission?: string;
  allowAdminBypass?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModuleAccessService {

  private static _allowedAccounts: AccountTypes[];
  // CVX-6068: lazy getter so AccountTypes is read at first use (runtime), not at
  // module-eval (class-definition). An eager static initializer here crashes the
  // Karma bundle via the @services barrel circular-init (AccountTypes undefined) —
  // the test-bundle residue of CVX-6069. Deferring the read fixes it with no test stub.
  public static get allowedAccounts(): AccountTypes[] {
    return this._allowedAccounts ?? (this._allowedAccounts = [AccountTypes.Dedicated, AccountTypes.Shared]);
  }

  constructor(
    private subscriberService: SubscriberService,
    private userDataService: UserdataService,
    private permissions: PermissionsService,
  ) {
  }

  public isModuleEnabled(moduleAlias: Module, checkRelatedFeatures?: boolean): boolean {
    return moduleAlias ? this.subscriberService.usesModule(moduleAlias.toString(), checkRelatedFeatures) : true;
  }

  public isFeatureEnabled(featureAlias: Feature, checkRelatedFeatures?: boolean): boolean {
    return featureAlias ? this.subscriberService.usesFeature(featureAlias, checkRelatedFeatures) : true;
  }

  public hasSubscriberAccess(subscriberAccess: IModuleSubscriberAccess, isNoPermissionsUser: boolean = false): boolean {
    if (isEmpty(subscriberAccess)) {
      // when it is empty, there are no restrictions
      return true;
    }

    let isAllowed = true;

    const usersIds: string[] = get(this.subscriberService.subInfo, `features.${subscriberAccess.user}`, []);
    if (usersIds.length) {
      if (!includes(usersIds, this.userDataService.userID)) {
        isAllowed = false;
      }
    }

    if (isAllowed && subscriberAccess.account) {
      const limit = get(this.subscriberService.subInfo, `features.${subscriberAccess.account}`, []);
      if (limit.length || isNoPermissionsUser) {
        // we are limiting by account type
        const accountAccess: AccountTypes[] = [
          ...limit,
          ...ModuleAccessService.allowedAccounts
        ];
        isAllowed = includes(accountAccess, this.userDataService.type);
      }
    }

    if (isAllowed && !isNoPermissionsUser && subscriberAccess.permission) {
      const limit = get(this.subscriberService.subInfo, `features.${subscriberAccess.permission}`, []);
      const elevatedPermissions: Permission[] = subscriberAccess.allowAdminBypass === false
        ? [Permission.Corvex, Permission.SuperAdmin]
        : [Permission.SuperAdmin, Permission.Admin, Permission.Corvex];

      // we are limiting by permission level
      let permissionAccess: Permission[] = [...elevatedPermissions];

      if (limit.length) {
        permissionAccess = [
          ...limit,
          ...elevatedPermissions
        ];
      }

      isAllowed = false;

      each(permissionAccess, perm => {
        if (has(this.userDataService.Permissions, perm) && this.userDataService.Permissions[perm]) {
          isAllowed = true;
        }
      });
    }

    return isAllowed;
  }

  public isAllowedAccount(): boolean {
    return includes(ModuleAccessService.allowedAccounts, this.userDataService.type);
  }

  public isAccountPermitted(subscriberAccess: IModuleSubscriberAccess) {
    const permissions = get(this.subscriberService.subInfo, `features.${subscriberAccess.account}`, []);
    return some(permissions, (permission) => this.permissions.canView(permission));
  }
}
